import React from 'react';
import { View, Text, Svg, Path, Image } from '@react-pdf/renderer';
import { styles } from './styles';
import { selectLogo, generateBarcode, getLogoDimensions } from './utils';

export function CheckMark({
  size = 12,
  color = '#000',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        fillRule="evenodd"
        d="M1.8501 12.0001C1.8501 6.39441 6.39441 1.8501 12.0001 1.8501C17.6058 1.8501 22.1501 6.39441 22.1501 12.0001C22.1501 17.6058 17.6058 22.1501 12.0001 22.1501C6.39441 22.1501 1.8501 17.6058 1.8501 12.0001ZM16.0639 10.672C16.52 10.3606 16.6373 9.73836 16.3258 9.28227C16.0144 8.82618 15.3922 8.70892 14.9361 9.02036L14.8348 9.08953C13.1939 10.21 11.7795 11.6244 10.6609 13.2575L9.20673 11.8049C8.816 11.4146 8.18283 11.4149 7.79252 11.8056C7.4022 12.1964 7.40254 12.8295 7.79327 13.2198L10.1345 15.5585C10.3549 15.7787 10.6653 15.8836 10.974 15.8422C11.2828 15.8008 11.5546 15.6179 11.7093 15.3475C12.7601 13.5099 14.2145 11.9349 15.9626 10.7412L16.0639 10.672Z"
        fill={color}
      />
    </Svg>
  );
}

export function CheckMarkCell({
  hasCheckMark,
  hasBorder = true,
}: {
  hasCheckMark: boolean;
  hasBorder?: boolean;
}) {
  return (
    <View
      style={{
        width: '30%',
        padding: 3,
        borderRightWidth: hasBorder ? 0.5 : 0,
        borderRightColor: '#000',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {hasCheckMark ? <CheckMark size={10} /> : null}
    </View>
  );
}

export function InfoRow({
  label,
  value,
  compact = false,
}: {
  label: string;
  value: string;
  compact?: boolean;
}) {
  return (
    <View
      style={[styles.infoRow, ...(compact ? [{ paddingVertical: 1 }] : [])]}
    >
      <Text style={styles.label}>{label}:</Text>
      <Text style={styles.labelData}>{value}</Text>
    </View>
  );
}

export function WrappingInfoRow({
  label,
  value,
  compact = false,
}: {
  label: string;
  value: string;
  compact?: boolean;
}) {
  return (
    <View
      style={[
        {
          paddingVertical: compact ? 1 : 3,
          flexDirection: 'row',
          alignItems: 'flex-start',
        },
      ]}
    >
      <Text style={[styles.label, { flexShrink: 0 }]}>{label}:</Text>
      <Text
        style={[
          styles.labelData,
          {
            marginLeft: 2,
            flex: 1,
            flexWrap: 'wrap',
          },
        ]}
      >
        {value}
      </Text>
    </View>
  );
}

export function TableRow({
  children,
  isLast = false,
}: {
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <View
      style={[
        styles.tableRow,
        isLast ? { borderBottomWidth: 0.5, borderBottomColor: '#000' } : {},
      ]}
    >
      {children}
    </View>
  );
}

export function CompactTableCell({
  width,
  children,
  hasBorder = true,
  textAlign = 'left',
  textStyle = {},
  padding = 1,
}: {
  width: string;
  children: React.ReactNode;
  hasBorder?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  textStyle?: Record<string, string | number>;
  padding?: number;
}) {
  let alignItems: 'flex-start' | 'center' | 'flex-end' = 'flex-start';
  if (textAlign === 'center') alignItems = 'center';
  if (textAlign === 'right') alignItems = 'flex-end';

  return (
    <View
      style={{
        width,
        padding,
        borderRightWidth: hasBorder ? 0.5 : 0,
        borderRightColor: '#000',
        justifyContent: 'center',
        alignItems,
      }}
    >
      <Text
        style={{
          fontSize: 5.5,
          fontWeight: 'normal',
          paddingVertical: 0,
          textAlign,
          ...textStyle,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

export function PDFLogo({
  division,
  logoWidth,
}: {
  division?: string;
  logoWidth?: number;
}) {
  const selectedLogo = selectLogo(division);
  const logoDimensions = getLogoDimensions(selectedLogo, logoWidth);

  return <Image src={selectedLogo} style={logoDimensions} />;
}

export function ProductImage({
  imageUrl,
  width = 80,
  height = 120,
  marginBottom = 5,
  objectFit = 'cover',
}: {
  imageUrl?: string;
  width?: number;
  height?: number;
  marginBottom?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}) {
  const containerStyle = {
    width,
    height,
    marginBottom,
    backgroundColor: '#f0f0f0',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    overflow: 'hidden' as const,
  };

  const imageStyle = {
    width,
    height,
    objectFit,
  };

  return imageUrl ? (
    <View style={containerStyle}>
      <Image src={imageUrl} style={imageStyle} />
    </View>
  ) : (
    <View style={containerStyle} />
  );
}

export function LinkIcon({ size = 10 }: { size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        d="M13.06 10.94a5 5 0 0 1 0 7.07l-3.54 3.54a5 5 0 0 1-7.07-7.07l1.77-1.77"
        stroke="#FFFFFF"
        strokeWidth={2.5}
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d="M10.94 13.06a5 5 0 0 1 0-7.07l3.54-3.54a5 5 0 0 1 7.07 7.07l-1.77 1.77"
        stroke="#FFFFFF"
        strokeWidth={2.5}
        strokeLinecap="round"
        fill="none"
      />
    </Svg>
  );
}

export function CoordinatingSeparatesBox({
  items,
}: {
  items: Array<{
    style_number: string;
    style_color?: string | null;
  }>;
}) {
  return (
    <View
      style={{
        backgroundColor: '#8B54F7',
        borderRadius: 6,
        paddingHorizontal: 8,
        paddingVertical: 6,
        marginTop: 6,
        width: '100%',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: 4,
          flexWrap: 'nowrap',
        }}
      >
        <Text
          style={{
            fontSize: 5.5,
            fontWeight: 'bold',
            color: '#FFFFFF',
            letterSpacing: 0,
            flexShrink: 0,
          }}
        >
          COORDINATING SEPARATES
        </Text>
        <View style={{ marginLeft: 3, flexShrink: 0 }}>
          <LinkIcon size={7} />
        </View>
      </View>
      {items.map((item) => (
        <Text
          key={item.style_color || item.style_number}
          style={{
            fontSize: 5.5,
            color: '#FFFFFF',
            marginBottom: 1,
          }}
        >
          {`\u2022 ${item.style_color || item.style_number}`}
        </Text>
      ))}
    </View>
  );
}

export function PDFBarcode({
  value,
  width = 110,
  height = 30,
  fontSize = 7,
}: {
  value?: string;
  width?: number;
  height?: number;
  fontSize?: number;
}) {
  const barcodeDataUrl = generateBarcode(value);

  return (
    <View style={styles.barcodeContainer}>
      <Image src={barcodeDataUrl} style={{ width, height }} />
      <Text style={[styles.lpnNumber, { fontSize }]}>{value || 'No LPN'}</Text>
    </View>
  );
}
